import { Delete as DeleteMuiIcon, Upload as UploadIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import ImageDraggable from 'src/components/image/image-draggable';
import LinearProgressBar from 'src/components/progress-bar/progressBar.component';
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
import HttpServices from 'src/services/httpService';
import { imageModeuleUrl, userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import Spinner from 'src/utils/Loader/spinner';
import { useToaster } from 'src/utils/toaster/toasterContext';
import NoMediaLibrary from '../../assets/app-image/no-media-library.svg';
import MediaFileComponent from './media-files.component';
import MediaUploadComponent from './media-upload.component';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useRightSidebar } from 'src/layouts/right-side-bar/rightSidebarProvider';
import ImageEditor from 'src/components/photo-editor/ImageEditor';

const MediaLibrary = ({ isFromNewPost = false, handleMediaFiles = null,handleNewPostEditFile }) => {
  const theme = useTheme();
  const { t: translate } = useTranslation();
  const POPUP_WIDTH = '1200px';
  const [loader, setLoader] = useState(true);
  const [height, setHeight] = useState(window.innerHeight - 190);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isShowUploadMedia, setUploadMedia] = useState(false);
  const [mediaData, setMediaData] = useState([]);
  const [showLoadMoreMedia, setShowLoadMoreMedia] = useState(false);
  const [isUploadLoading, setUploadLoading] = useState(false);
  const [fileUploadprogress, setFileUploadprogress] = useState(0);
  const [deleteSelectedConfirmOpen, setDeleteSelectedConfirmOpen] = useState(false);
  const { showRightSidebar, handleCloseRightSidebar } = useRightSidebar();

  const { isCollapse } = useCollapseDrawer();
  const navigate = useNavigate();
  const { showToast } = useToaster();

  useEffect(() => {
    getAllMedia();
  }, []);

  const getAllMedia = (isRefresh = false) => {
    let mediaProps = {
      url: userModuleURL.getAllMedia,
      successCallback: (data, message) => successCallback(data, message, isRefresh),
      failureCallback,
    };
    HttpServices.Get(mediaProps);
  };
  const successCallback = (data, message, isRefresh = false) => {
    setMediaUrls(data);
    if (data.length > 0) {
      setShowLoadMoreMedia(true);
    } else {
      setShowLoadMoreMedia(false);
    }
    prepareMediaData(data, isRefresh);
  };
 const prepareMediaData = (files, isRefresh = false) => {
  if (isRefresh) {
    setMediaData([]);
    // We'll process from scratch below
  }

  const noOfImages = 20;
  const startIndex = isRefresh ? 0 : mediaData.length;

  // We want items from the END of the array (newest first)
  // Skip already loaded ones → take next batch
  const endIndex = files.length;                    // last item (newest)
  const startProcessingAt = files.length - 1 - startIndex;   // where to start this batch
  const stopAt = Math.max(-1, files.length - 1 - startIndex - noOfImages); // inclusive

  let itemsToProcess = 0;
  for (let i = startProcessingAt; i >= 0 && i > stopAt; i--) {
    itemsToProcess++;
  }

  if (itemsToProcess === 0) {
    setShowLoadMoreMedia(false);
    setLoader(false);
    return;
  }

  let processedItems = 0;

  for (let i = startProcessingAt; i >= 0 && i > stopAt; i--) {
    const media = files[i];

    HttpServices.GetImageFromAPI(media.thumbnail_url).then(thumbnail_url => {
      const processedMedia = { ...media, thumbnail_url };
      processedItems++;

      setMediaData(prev => {
        // Prevent duplicates just in case
        if (prev.some(item => item.media_url === processedMedia.media_url)) {
          return prev;
        }
        // Add to end → maintains newest first order
        return [...prev, processedMedia];
      });

      if (processedItems === itemsToProcess) {
        // If we got fewer than requested → no more to load
        if (itemsToProcess < noOfImages) {
          setShowLoadMoreMedia(false);
        }
        setLoader(false);
      }
    }).catch(err => {
      console.error("Failed to load thumbnail", err);
      processedItems++;
      // still count it so we don't hang
      if (processedItems === itemsToProcess) setLoader(false);
    });
  }

  // Optional: early update of load more button
  if (startProcessingAt - noOfImages < 0) {
    setShowLoadMoreMedia(false);
  }
};

  const loadMoreMedia = () => {
    prepareMediaData(mediaUrls, false);
  };

  const deleteMediaSuccessCallback = (data, message) => {
    message && showToast(message, 's');
  };
  const failureCallback = (message) => {
    message && showToast(message, 'e');
    setLoader(false);
  };
  const getMediaPath = (media) => {
    try {
      const urlObj = new URL(media.media_url, window.location.origin);
      return urlObj.searchParams.get('path');
    } catch (error) {
      return null;
    }
  };

  const handleDeleteMedia = (media) => {
    handleDeleteMediaFiles([media]);
  };

  const handleDeleteMediaFiles = (files) => {
    const mediaPaths = files.map(getMediaPath).filter(Boolean);
    if (mediaPaths.length === 0) return;

    const deleteUrls = files.map((file) => file.media_url);
    setMediaData((prev) => prev.filter((media) => !deleteUrls.includes(media.media_url)));
    setMediaUrls((prev) => prev.filter((media) => !deleteUrls.includes(media.media_url)));
    setSelectedFiles((prev) => prev.filter((media) => !deleteUrls.includes(media.media_url)));

    let media_urls = {
      media_paths: mediaPaths,
    };
    let mediaProps = {
      url: imageModeuleUrl.deleteMedia,
      body: media_urls,
      successCallback: deleteMediaSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(mediaProps);
  };

  const handleDeleteSelectedMedia = () => {
    handleDeleteMediaFiles(selectedFiles);
    setDeleteSelectedConfirmOpen(false);
  };
  const handleEditMedia = (media) => {
    let privateFiles = [media];
    const uploadFilesProps = {
      privateFiles,
      successCallback: fetchEditMediaSuccessCallback,
      failureCallback,
    };
    CommonHelper.UploadPrivateMediaFilesToServer({ ...uploadFilesProps });
  };
  const handleEditFile = (file) => {
    let data = {
      content: () => (
        <ImageEditor
          showToast={showToast}
          translate={translate}
          handleUploadEditedImage={handleUploadEditedImage}
          handleCloseImageEditor={handleCloseImageEditor}
          theme={theme}
          imageUrl={file.media_url}
        />
      ),
    };
    showRightSidebar(data, POPUP_WIDTH);
  };
  const handleCloseUploadMedia = () => {
    setUploadMedia(false);
  };
  const handleSelectFile = (event, media) => {
    let files = [...selectedFiles];
    let isChecked = event.target.checked;
    if (isChecked) {
      files.push(media);
    } else {
      let index = files.findIndex((a) => a.media_url === media.media_url);
      if (index >= 0) {
        files.splice(index, 1);
      }
    }
    setSelectedFiles(files);
    if (isFromNewPost) {
      handleMediaFiles(media, isChecked);
    }
  };
  const handleMediaUpload = (files) => {
    let mFiles = [...mediaFiles];
    mFiles = mFiles.concat(files);
    setMediaFiles([...mFiles]);
  };
  const handleReOrderFiles = (oldIndex, newIndex) => {
    setMediaFiles((prevImages) => {
      const newImages = [...prevImages];
      return CommonHelper.ReArrangeArray(newImages, oldIndex, newIndex);
    });
  };
  const handleRemoveFiles = (file) => {
    let files = [...mediaFiles];
    let index = files.findIndex((a) => a.path === file.path);
    if (index >= 0) files.splice(index, 1);
    setMediaFiles([...files]);
  };
  const fileUploadSuccessCallback = () => {
    setMediaFiles([]);
    handleCloseUploadMedia();
    getAllMedia(true); 
  };
  const handleUploadMediaFiles = () => {
    if (mediaFiles?.length > 0) {
      setUploadLoading(true);
      setFileUploadprogress(1);
      let formData = new FormData();
      for (var i = 0; i < mediaFiles.length; i++) {
        formData.append('fileupload', mediaFiles[i]);
      }
      let mediaProps = {
        formData,
        setFileUploadprogress,
        successCallback: fileUploadSuccessCallback,
        failureCallback,
      };
      HttpServices.UploadMediaFiles(mediaProps);
    } else {
      handleCloseUploadMedia();
    }
  };
  const serverUploadSuccessCallback = (data, message) => {
    message && showToast(message);
  };
  const handleUploadMediaNewPost = () => {
    let privateFiles = [...selectedFiles];
    const uploadFilesProps = {
      privateFiles,
      successCallback: fetchMediaSuccessCallback,
      failureCallback,
    };
    CommonHelper.UploadPrivateMediaFilesToServer({ ...uploadFilesProps });
  };
  const fetchMediaSuccessCallback = (data, message) => {
    const updatedData = {
      media_data: data.media_datas,
    };
    navigate('/new-post', { state: { media_data: updatedData } });
  };
  const fetchEditMediaSuccessCallback = (data, message) => {
    if (data?.media_datas?.length > 0 && !isFromNewPost) {
      handleEditFile(data.media_datas[0]);
    }else{
      handleNewPostEditFile(data.media_datas[0])
    }
  };
  const handleCloseImageEditor = () => {
    handleCloseRightSidebar();
  };
  const handleUploadEditedImage = () => {
    handleCloseRightSidebar();
    getAllMedia(true); // Use refresh mode to maintain order
  };

  const handleUploadToServer = () => {
    if (selectedFiles?.length > 0) {
      setFileUploadprogress(1);
      let formData = new FormData();
      
      // Process files sequentially to avoid async complexity
      const processFiles = () => {
        let processedCount = 0;
        selectedFiles.forEach((selectedFile, index) => {
          CommonHelper.GetFileFromUrl(selectedFile.media_url).then(file => {
            formData.append('fileupload', file);
            processedCount++;
            
            // Upload when all files are processed
            if (processedCount === selectedFiles.length) {
              let mediaProps = {
                formData,
                setFileUploadprogress,
                successCallback: serverUploadSuccessCallback,
                failureCallback,
              };
              HttpServices.UploadMediaFiles(mediaProps);
            }
          });
        });
      };
      
      processFiles();
    }
  };
  return (
    <>
      <Helmet>
        <title>{translate('navbar.menu.media_library')}</title>
      </Helmet>
      {isFromNewPost &&
        (loader ? (
          <Spinner />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
              gap: 2,
              p: 2,
            }}
          >
            {mediaUrls?.length > 0 && mediaData?.length > 0 ? (
              <>
                <MediaFileComponent
                  mediaData={mediaData}
                  handleDelete={handleDeleteMedia}
                  handleUpload={handleUploadToServer}
                  handleSelectFile={handleSelectFile}
                  selectedFiles={selectedFiles}
                  isFromNewPost={isFromNewPost}
                  loadMoreMedia={loadMoreMedia}
                  showLoadMoreMedia={showLoadMoreMedia}
                  handleEdit={handleEditMedia}
                />
                {isFromNewPost && fileUploadprogress > 0 && <LinearProgressBar value={fileUploadprogress} />}
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <img src={NoMediaLibrary} alt="NoMediaLibrary" width={'100%'} />
              </Box>
            )}
          </Box>
        ))}
      {!isFromNewPost &&
        (loader ? (
          <Spinner />
        ) : (
          <>
            <Card sx={{ height: 'calc(100vh - 190px)' }}>
              {mediaUrls?.length > 0 && mediaData?.length > 0 ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                      gap: 2,
                      p: 2,
                      maxHeight: 'calc(100vh - 180px)',
                      overflow: 'auto',
                    }}
                  >
                    <MediaFileComponent
                      mediaData={mediaData}
                      handleDelete={handleDeleteMedia}
                      handleUpload={handleUploadToServer}
                      handleSelectFile={handleSelectFile}
                      selectedFiles={selectedFiles}
                      isFromNewPost={isFromNewPost}
                      loadMoreMedia={loadMoreMedia}
                      handleEdit={handleEditMedia}
                      showLoadMoreMedia={showLoadMoreMedia}
                    />
                  </Box>
                  {isFromNewPost && fileUploadprogress > 0 && <LinearProgressBar value={fileUploadprogress} />}
                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img src={NoMediaLibrary} alt="NoMediaLibrary" height={height} />
                </Box>
              )}
            </Card>
            <Card style={{ width: '100%', marginTop: '1rem', padding: '0.7rem' }} isCollapse={isCollapse}>
              <Stack
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ height: '3rem' }}
              >
                <LoadingButton
                  variant="contained"
                  endIcon={<UploadIcon sx={{ width: '20px', height: '20px' }} />}
                  onClick={() => setUploadMedia(true)}
                >
                  {translate('media_library.upload_to_media_library')}
                </LoadingButton>
                {selectedFiles?.length > 0 && (
                  <Stack direction="row" spacing={1}>
                    <LoadingButton
                      variant="outlined"
                      color="error"
                      endIcon={<DeleteMuiIcon sx={{ width: '20px', height: '20px' }} />}
                      onClick={() => setDeleteSelectedConfirmOpen(true)}
                    >
                      {translate('media_library.delete_selected_media')} ({selectedFiles.length})
                    </LoadingButton>
                    <LoadingButton
                      variant="contained"
                      endIcon={<UploadIcon sx={{ width: '20px', height: '20px' }} />}
                      onClick={handleUploadMediaNewPost}
                    >
                      {translate('media_library.upload_image_to_new_post')}
                    </LoadingButton>
                  </Stack>
                )}
              </Stack>
            </Card>
          </>
        ))}

      {/* Popup Place */}
      {isShowUploadMedia && (
        <Dialog open={isShowUploadMedia} onClose={handleCloseUploadMedia}>
          <DialogTitle>{translate('media_library.upload_to_image_library')}</DialogTitle>
          <DialogContent>
            <MediaUploadComponent handleMediaFiles={handleMediaUpload} isUploadToImageServer={false} />
            {mediaFiles?.length > 0 && (
              <Box>
                <ImageDraggable
                  mediaFiles={mediaFiles}
                  handleRemoveFiles={handleRemoveFiles}
                  handleReOrderFiles={handleReOrderFiles}
                  isFile={true}
                />
              </Box>
            )}
            {fileUploadprogress > 0 && <LinearProgressBar value={fileUploadprogress} />}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="error" onClick={handleCloseUploadMedia}>
              {translate('common.cancel')}
            </Button>
            <LoadingButton variant="contained" isLoading={isUploadLoading} onClick={handleUploadMediaFiles} autoFocus>
              {translate('common.upload')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
      <Dialog open={deleteSelectedConfirmOpen} onClose={() => setDeleteSelectedConfirmOpen(false)}>
        <DialogTitle>{translate('common.confirm_delete')}</DialogTitle>
        <DialogContent>
          {translate('media_library.delete_selected_media_confirmation', { count: selectedFiles.length })}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={() => setDeleteSelectedConfirmOpen(false)}>
            {translate('common.cancel')}
          </Button>
          <Button color="error" onClick={handleDeleteSelectedMedia} autoFocus>
            {translate('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default MediaLibrary;
